import { MILESTONES, COMPANY, BRANDS } from '../data/content';
import SEO from '../components/SEO';
import { Target, Shield, Lightbulb, Globe } from 'lucide-react';

const VALUES = [
  { icon: Target,    title: 'Precision First',  desc: 'Every instrument calibrated to national & international standards.' },
  { icon: Shield,    title: 'Lifetime Support',  desc: '2-year warranty on every instrument we ship.' },
  { icon: Lightbulb, title: 'Innovation',        desc: "From the world's first stationary spindle concentricity device to digital gauges — we solve unsolved problems." },
  { icon: Globe,     title: 'Global Reach',      desc: 'Trusted in 18+ countries. Our instruments speak the universal language of quality.' },
];

export default function AboutPage() {
  return (
    <>
      <SEO title="About" description="Apex Precitech — Thirty years of precision instruments for textile spinning machinery. Based in Coimbatore, India since 1996." />
      <div data-testid="about-page" className="pt-16 min-h-screen bg-white">

      {/* ── Compact header ── */}
      <section className="py-10 md:py-12 border-b border-steel-200" style={{ background: '#FAFAF8' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-[2px] bg-safety" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-safety">Our Story</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-steel-900 uppercase tracking-tight">
            About Apex Precitech
          </h1>
        </div>
      </section>

      {/* ── Story + Values — single row section ── */}
      <section className="py-12 md:py-14 border-b border-steel-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">

            {/* Story — 3 of 5 cols */}
            <div className="lg:col-span-3">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-steel-900 uppercase tracking-tight mb-6">
                Thirty Years of Precision
              </h2>
              <div className="space-y-4 font-body text-steel-500 text-[15px] leading-relaxed">
                <p>
                  The Apex saga of manufacturing instruments began in 1996, when we set out to build the
                  Top Arm Load Gauge — offering direct reading in kilograms, angle-related measurement of
                  loads in top arms, and accuracy the industry had never seen before.
                </p>
                <p>
                  Our dedicated work with mills continuously refined the product across five successive
                  models over thirty years, culminating in the{' '}
                  <strong className="text-steel-800">Digital Top Arm Load Gauge in 2026.</strong>
                </p>
                <p>
                  Simultaneously, the same probing mentality extended to every parameter affecting
                  industrial health — Draw Frame gauges, Comber gauges, Ring Spindle Concentricity
                  devices, Saddle Setting devices, and the Dew Point Indicator all followed.
                </p>
                <p className="font-medium text-steel-700 border-l-2 border-safety pl-4 italic">
                  The journey continues, and will go on forever.
                </p>
              </div>
            </div>

            {/* Values — 2 of 5 cols */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-steel-900 uppercase tracking-tight mb-6">
                What Drives Us
              </h2>
              <div className="space-y-5">
                {VALUES.map((v) => (
                  <div key={v.title} className="flex items-start gap-3.5">
                    <div className="w-8 h-8 flex items-center justify-center border border-steel-200 flex-shrink-0 mt-0.5">
                      <v.icon size={15} className="text-safety" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm uppercase tracking-wide text-steel-900 mb-0.5">{v.title}</h3>
                      <p className="font-body text-steel-500 text-sm leading-relaxed">{v.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Milestones — compact horizontal scroll on mobile, 3-col grid on desktop ── */}
      <section className="py-12 md:py-14 border-b border-steel-200" style={{ background: '#FAFAF8' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-8 h-[2px] bg-safety" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel-400">30 Years</span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-steel-900 uppercase tracking-tight">
              Milestones
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {MILESTONES.map((m) => {
              const isLatest = m.year === '2026';
              return (
                <div
                  key={m.year}
                  className={`milestone-card p-5 bg-white border-2 relative overflow-hidden ${
                    isLatest ? 'border-safety' : 'border-steel-200'
                  }`}
                >
                  {/* Watermark year */}
                  <div
                    className="absolute right-2 bottom-1 font-heading font-bold select-none pointer-events-none leading-none"
                    style={{
                      fontSize: '56px',
                      color: isLatest ? 'rgba(249,115,22,0.07)' : 'rgba(15,23,42,0.04)',
                    }}
                  >
                    {m.year}
                  </div>
                  {/* Accent */}
                  <div className={`w-6 h-0.5 mb-3 ${isLatest ? 'bg-safety' : 'bg-steel-200'}`} />
                  {/* Year */}
                  <span className={`font-heading text-xl md:text-2xl font-bold block mb-2 ${isLatest ? 'text-safety' : 'text-steel-900'}`}>
                    {m.year}
                  </span>
                  {/* Text */}
                  <p className="font-body text-steel-600 text-sm leading-relaxed relative z-10">{m.text}</p>
                  {isLatest && (
                    <span className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-wider bg-safety text-white px-2 py-0.5">
                      Latest
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Brands — compact ── */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-400 mb-5">
            Compatible with all major manufacturers
          </p>
          <div className="flex flex-wrap justify-center gap-5 md:gap-9">
            {BRANDS.map(brand => (
              <span
                key={brand}
                className="font-heading text-base md:text-lg uppercase tracking-wider text-steel-300 hover:text-steel-600 transition-colors cursor-default"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
    </>
  );
}